import { Stack } from '@startuptools/ui/Stack';
import { Modal } from '@startuptools/ui/Modal';
import { ModalDialog } from '@startuptools/ui/ModalDialog';
import { ModalClose } from '@startuptools/ui/ModalClose';
import { Typography } from '@startuptools/ui/Typography';
import { Box } from '@startuptools/ui/Box';
import { useOsrVestedAndAllocatedSuspenseQuery } from './options-subscribers.graphql';
import { Trans } from '@lingui/react';
import { Identity } from '@startuptools/business-ui/identity/Identity';
import React, { type ComponentProps } from 'react';
import { useCompanyId } from '@startuptools/common/react-wrapper';
import {
  type GqlWarrantProgram,
  type GqlOptionsSharesRegistration,
  type GqlOptionsSharesSubscriber,
} from '../../../../../../graphql/base-types.graphql';
import { SelectOptionsSubscriberDialog } from './SelectOptionsSubscriberTable';
import { OptionsSharesSubscriberForm } from './OptionsSharesSubscriberForm';

const Content = ({
  warrantProgram,
  optionsSharesRegistration,
  onClose,
  oss,
}: {
  warrantProgram: Pick<GqlWarrantProgram, 'id' | 'warrantStartDate' | 'warrantKind' | 'usesStepper' | 'shareClasses'>;
  onClose: () => void;
  optionsSharesRegistration: Pick<GqlOptionsSharesRegistration, 'id' | 'recordDay'>;
  oss?: Pick<GqlOptionsSharesSubscriber, 'id' | 'identityId' | 'shareClass' | 'quantity'> | null | undefined;
}) => {
  const companyId = useCompanyId();
  const {
    data: { osrVestedAndAllocated },
  } = useOsrVestedAndAllocatedSuspenseQuery({
    variables: { companyId, id: optionsSharesRegistration.id },
    fetchPolicy: 'no-cache',
  });

  const [selected, setSelected] = React.useState<{ identityId: string; shareClassName: string } | null>(
    oss
      ? {
          identityId: oss.identityId,
          shareClassName: oss.shareClass.name,
        }
      : null,
  );

  const { warrantStartDate } = warrantProgram;

  if (!warrantStartDate) {
    throw new Error('cant select options without warrant start date');
  }

  const selectedRow = osrVestedAndAllocated.find(
    m => m.identity.id === selected?.identityId && m.shareClass.name === selected?.shareClassName,
  );

  if (selectedRow) {
    return (
      <Stack spacing={2}>
        <Identity identity={selectedRow.identity} />
        <OptionsSharesSubscriberForm
          osrId={optionsSharesRegistration.id}
          identity={selectedRow.identity}
          oss={oss}
          shareClassName={selectedRow.shareClass.name}
          vestedAndAllocated={osrVestedAndAllocated}
          onClose={onClose}
        />
      </Stack>
    );
  }
  return <SelectOptionsSubscriberDialog osrId={optionsSharesRegistration.id} setSelected={setSelected} />;
};

export const OptionsSharesSubscriberDialog = ({ onClose, oss, ...props }: ComponentProps<typeof Content>) => {
  return (
    <Modal open onClose={onClose}>
      <ModalDialog sx={{ overflowY: 'auto' }}>
        <ModalClose />
        <Box paddingY={2}>
          <Stack gap={3}>
            <Typography>{oss ? <Trans id="Edit Subscriber" /> : <Trans id="Add Subscriber" />}</Typography>
            <Content {...props} onClose={onClose} oss={oss} />
          </Stack>
        </Box>
      </ModalDialog>
    </Modal>
  );
};
